// Chat persistence client for sutrumai.com.
//
// EVERY call hits the server — there is no local-store fallback any
// more. The server identifies the owner by either a Bearer token
// (signed-in user) or the X-Anon-Uid header (anon browser, value
// minted by getAnonUserId() and persisted in localStorage as
// arthum.uid.v1). Same DynamoDB table either way, just a different
// value in the partition key.
//
// On sign-in the App calls claimAnonChats() which atomically rewrites
// every anon-owned row to be owned by the new email and strips the
// 90-day TTL — making the user's prior anon history permanent.

import { getToken } from './auth'
import { getAnonUserId } from './localChats'

const API_BASE = (process.env.REACT_APP_API_BASE || '').replace(/\/$/, '')

// Send Bearer when signed in, otherwise X-Anon-Uid. Server's
// resolveOwner() prefers the Bearer (so a stale uid header on a
// signed-in client doesn't break ownership).
async function ownerFetch(path, opts = {}) {
  const token = getToken()
  const uid = token ? null : getAnonUserId()  // mints + persists on first call
  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(token
        ? { Authorization: `Bearer ${token}` }
        : (uid ? { 'X-Anon-Uid': uid } : {})),
      ...(opts.headers || {}),
    },
  })
  let data = null
  try { data = await res.json() } catch {}
  if (!res.ok) {
    const err = new Error(data?.error?.message || `HTTP ${res.status}`)
    err.status = res.status
    err.code = data?.error?.code
    throw err
  }
  return data
}

// scope: 'chat' | 'assistant' | undefined.
export async function listChats({ scope } = {}) {
  const qs = scope ? `?scope=${encodeURIComponent(scope)}` : ''
  const data = await ownerFetch(`/api/deepai/chats${qs}`)
  return data.chats || []
}

export async function loadChat(chatId) {
  const data = await ownerFetch(`/api/deepai/chats/${encodeURIComponent(chatId)}`)
  return data.chat
}

// Pass-through for scope/assistantId/assistantName so the first save
// of an assistant-spawned chat is tagged correctly. Server ignores
// these on subsequent updates (scope is frozen at create time).
export async function saveChat(chatId, body) {
  const data = await ownerFetch(`/api/deepai/chats/${encodeURIComponent(chatId)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
  return data.chat
}

export async function renameChat(chatId, title) {
  const data = await ownerFetch(`/api/deepai/chats/${encodeURIComponent(chatId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ title }),
  })
  return data.chat
}

export async function deleteChat(chatId) {
  await ownerFetch(`/api/deepai/chats/${encodeURIComponent(chatId)}`, { method: 'DELETE' })
}

// Bulk-delete every chat in the given scope (or all chats when scope
// is omitted). Returns the number that were removed. Parallel deletes
// via allSettled so one transient 5xx doesn't abort the sweep.
export async function clearAllChats({ scope } = {}) {
  const all = await listChats({ scope })
  if (!all.length) return 0
  await Promise.allSettled(all.map((c) => deleteChat(c.chatId)))
  return all.length
}

// Called right after a successful sign-in. Sends BOTH Bearer token
// and X-Anon-Uid so the server proves the user owns the anon UID.
// Server transfers every chat keyed by the anon UID to be keyed by
// the user's email and strips the TTL. Idempotent — second call is a
// no-op (no chats left under that UID).
export async function claimAnonChats() {
  const token = getToken()
  if (!token) return { claimed: 0 }
  let uid = null
  try { uid = getAnonUserId() } catch {}
  if (!uid) return { claimed: 0 }
  const res = await fetch(`${API_BASE}/api/deepai/chats/claim`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'X-Anon-Uid': uid,
    },
  })
  let data = null
  try { data = await res.json() } catch {}
  if (!res.ok) {
    const err = new Error(data?.error?.message || `HTTP ${res.status}`)
    err.status = res.status
    throw err
  }
  return data || { claimed: 0 }
}

// IDs minted client-side. lc-* keeps the anon-era prefix so chat ids
// minted on this device are easy to spot in DDB if you go looking.
// Server doesn't care — the value is just opaque to it.
export function newChatId() {
  const rand = Math.random().toString(36).slice(2, 10)
  return getToken()
    ? `c-${Date.now().toString(36)}-${rand}`
    : `lc-${Date.now().toString(36)}-${rand}`
}
